import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import axios from 'axios';
import logger from '../core/logger/logger';

/**
 * Переводы (v1.49.0). Все тексты — ключи; бэк отдаёт ОДИН файл на язык со всеми ключами
 * (`GET /v1/i18n/{lang}` → {lang, version, translations}). В коде всегда английский дефолт:
 * `t(key, 'English text')` — он показывается, пока файл не загрузился или ключа ещё нет в базе.
 *
 * Язык: выбранный в Settings (localStorage + `users.language`) → Telegram language_code → en.
 * Неизвестный бэку язык он сам заменит на дефолтный (lang в ответе).
 *
 * Новые ключи из кода: в dev-сборке (или REACT_APP_I18N_SAVE_MISSING=true) отсутствующие ключи
 * отправляются в `POST /v1/i18n/missing` пачками, бэк создаёт их с английским текстом из кода.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.datum-games.com';
const LANG_STORAGE_KEY = 'datum.lang';
const BUNDLE_STORAGE_PREFIX = 'datum.i18n.';
const SAVE_MISSING =
  process.env.NODE_ENV === 'development' || process.env.REACT_APP_I18N_SAVE_MISSING === 'true';

// Ключи сущностей создаёт бэк (I18nEntityKeys) — с фронта их не шлём.
const ENTITY_PREFIXES = [
  'achievement_config.', 'battle_pass_season.', 'character_module.', 'character.', 'chest_type.', 'key_type.',
  'datum_pass_product.', 'lair_building.', 'lair_resource.', 'mission_config.', 'module_rank.', 'reward.',
  'shop_boost.', 'shop_item.', 'shop_pack.', 'task.', 'merge_fragment.', 'server.', 'bot.',
];

const safeStorage = {
  get(key) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  set(key, value) {
    try { window.localStorage.setItem(key, value); } catch { /* приватный режим / квота */ }
  },
  remove(key) {
    try { window.localStorage.removeItem(key); } catch { /* ignore */ }
  },
};

/** Язык, выбранный игроком в Settings (null — следовать Telegram). */
export const getChosenLanguage = () => safeStorage.get(LANG_STORAGE_KEY);

const detectLanguage = () => getChosenLanguage() || getTelegramLanguage();

/** Двухбуквенный код языка Telegram-клиента (или браузера), без сведения к ru/en. */
export const getTelegramLanguage = () => {
  const code =
    window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code ||
    (typeof navigator !== 'undefined' ? navigator.language : '') ||
    'en';
  return String(code).toLowerCase().split(/[-_]/)[0] || 'en';
};

// Языки, файл которых пришёл с бэка: до загрузки любой ключ «отсутствует» — такие не репортим.
const loadedFromServer = new Set();

const readCachedBundle = (lang) => {
  const raw = safeStorage.get(BUNDLE_STORAGE_PREFIX + lang);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

/** Загружает файл языка с бэка; возвращает фактический язык (бэк мог заменить неизвестный на дефолтный). */
export async function loadLanguage(lang) {
  const cached = readCachedBundle(lang);
  if (cached?.translations) {
    i18n.addResourceBundle(cached.lang || lang, 'translation', cached.translations, true, true);
  }
  try {
    const { data } = await axios.get(`${API_BASE_URL}/v1/i18n/${encodeURIComponent(lang)}`, { timeout: 10000 });
    const actual = data?.lang || lang;
    if (data?.translations) {
      i18n.addResourceBundle(actual, 'translation', data.translations, true, true);
      safeStorage.set(BUNDLE_STORAGE_PREFIX + actual, JSON.stringify(data));
      loadedFromServer.add(actual);
    }
    return actual;
  } catch (e) {
    logger.warn('[i18n] failed to load language file', { lang, message: e?.message });
    return cached?.lang || lang;
  }
}

// ---- dev: отсутствующие ключи → бэк ----
const missingQueue = new Map();
let missingTimer = null;
const flushMissing = () => {
  missingTimer = null;
  const keys = [...missingQueue.entries()].map(([key, defaultText]) => ({ key, defaultText }));
  missingQueue.clear();
  for (let i = 0; i < keys.length; i += 50) {
    axios.post(`${API_BASE_URL}/v1/i18n/missing`, { keys: keys.slice(i, i + 50) }).catch(() => {});
  }
};
const reportMissing = (key, defaultText) => {
  if (!SAVE_MISSING || !key || !defaultText || !loadedFromServer.has(i18n.language)) return;
  if (ENTITY_PREFIXES.some((p) => key.startsWith(p))) return;
  missingQueue.set(key, defaultText);
  if (!missingTimer) missingTimer = setTimeout(flushMissing, 2000);
};

let initPromise = null;

/** Инициализация (один раз): сначала английские дефолты из кода, затем файл языка с бэка. */
export function initI18n() {
  if (initPromise) return initPromise;
  const lang = detectLanguage();
  i18n.use(initReactI18next).init({
    lng: lang,
    fallbackLng: false,
    resources: {},
    partialBundledLanguages: true,
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    returnNull: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false, bindI18n: 'languageChanged loaded', bindI18nStore: 'added' },
    saveMissing: SAVE_MISSING,
    missingKeyHandler: (_lngs, _ns, key, fallbackValue) => reportMissing(key, fallbackValue),
  });
  initPromise = loadLanguage(lang).then(async (actual) => {
    if (actual !== i18n.language) await i18n.changeLanguage(actual);
    document.documentElement.lang = actual;
    return actual;
  });
  return initPromise;
}

/**
 * Сменить язык: грузим файл, переключаем UI. persist=true — выбор игрока в Settings
 * (null/'' — вернуться к языку Telegram).
 */
export async function changeLanguage(lang, { persist = false } = {}) {
  if (persist) {
    if (lang) safeStorage.set(LANG_STORAGE_KEY, lang);
    else safeStorage.remove(LANG_STORAGE_KEY);
  }
  const target = lang || getTelegramLanguage();
  const actual = await loadLanguage(target);
  await i18n.changeLanguage(actual);
  document.documentElement.lang = actual;
  return actual;
}

/** Текущий язык для заголовка Accept-Language. */
export const currentLanguage = () => i18n.language || detectLanguage();

export default i18n;
